import React, { useState, useCallback, useRef, useEffect } from 'react';
import apiClient, { api } from '../utils/apiClient';
import {
  Download, FileText, Upload, Search, DollarSign, Loader2, X,
  Calculator, FileDown, Trash2, Lock, RotateCcw,
  AlertCircle, CheckCircle, Users, Edit3, Save,
} from 'lucide-react';

// ─── helpers ──────────────────────────────────────────────────────────────────
const formatCOP = (v) => {
  const n = Number(v);
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency', currency: 'COP', maximumFractionDigits: 0,
  }).format(n);
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const TABS = [
  { id: 'facturados', label: 'Casos Facturados',      icon: FileText   },
  { id: 'pagados',    label: 'Casos Pagados',         icon: CheckCircle },
  { id: 'cuentas',    label: 'Cuentas de Cobro',      icon: DollarSign },
  { id: 'abogados',   label: 'Abogados',               icon: Users      },
  { id: 'documentos', label: 'Documentos de Soporte', icon: Upload     },
];

// ─── shared card style ─────────────────────────────────────────────────────────
const cardCls = 'bg-slate-900/90 rounded-2xl border border-slate-700/50 shadow-xl';
const inputCls = 'bg-slate-800 border border-slate-700 rounded-xl px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-colors duration-200';
const btnPrimary = 'flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg shadow-blue-900/20 active:scale-95';
const btnGreen   = 'flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-all duration-200 shadow-lg active:scale-95';


// ─── component ────────────────────────────────────────────────────────────────
const Contabilidad = ({ user }) => {
  const rol = user?.rol || user?.role;

  // ── global UI ────────────────────────────────────────────────────────────────
  const [tab,     setTab]     = useState('facturados');
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');

  // ── Tab 1 – Casos Facturados ─────────────────────────────────────────────────
  const [casos,        setCasos]        = useState([]);
  const [loadingCasos, setLoadingCasos] = useState(false);
  const [fechaDesde,   setFechaDesde]   = useState(firstOfMonth);
  const [fechaHasta,   setFechaHasta]   = useState(todayStr);
  const [busqueda,     setBusqueda]     = useState('');
  const [loadingExcel, setLoadingExcel] = useState(false);
  // Paginacion casos facturados (Diner 2026-04-27)
  const [currentPageCasos, setCurrentPageCasos] = useState(1);
  const ITEMS_PER_PAGE_CASOS = 50;

  // ── Tab 2 – Cuentas de Cobro ─────────────────────────────────────────────────
  const [ccInput,        setCcInput]        = useState('');
  const [cuentasData,    setCuentasData]    = useState(null);
  const [loadingCuentas, setLoadingCuentas] = useState(false);
  // upload PDF cuenta de cobro (Diner 2026-04-27)
  const [ccUploadFile,   setCcUploadFile]   = useState(null);
  const [ccUploading,    setCcUploading]    = useState(false);
  const ccUploadInputRef = useRef(null);

  // ── Tab 3 – Abogados ─────────────────────────────────────────────────────────
  const [abogadosList,    setAbogadosList]    = useState([]);
  const [loadingAb,       setLoadingAb]       = useState(false);
  const [editingNumDoc,   setEditingNumDoc]   = useState(null);
  const [editConsecutivo, setEditConsecutivo] = useState(1);
  const [savingAb,        setSavingAb]        = useState(false);
  const [busquedaAb,      setBusquedaAb]      = useState('');
  // carga masiva
  const [bulkFile,        setBulkFile]        = useState(null);
  const [loadingBulk,     setLoadingBulk]     = useState(false);
  const [bulkResult,      setBulkResult]      = useState(null);
  const bulkInputRef = useRef(null);

  // ── Tab 4 – Documentos de Soporte ───────────────────────────────────────────
  const [docCC,          setDocCC]          = useState('');
  const [docFile,        setDocFile]        = useState(null);
  const [docDescripcion, setDocDescripcion] = useState('');
  const [loadingDoc,     setLoadingDoc]     = useState(false);
  const [docsList,       setDocsList]       = useState(null);
  const [loadingDocsList,setLoadingDocsList]= useState(false);
  const fileInputRef = useRef(null);

  // ── load casos ───────────────────────────────────────────────────────────────
  const loadCasos = useCallback(async () => {
    setLoadingCasos(true);
    setError('');
    try {
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const data = await api.get('/facturacion-legal-detalle', { params });
      const raw = Array.isArray(data) ? data : (data.casos || data.data || []);
      setCasos(raw.map(c => {
        const lower = (c.aseguradora || '').toLowerCase().trim();
        if (lower.includes('asis') && lower.includes('prex')) return { ...c, aseguradora: 'Assisprex' };
        return c;
      }));
      // (sin selección)
    } catch {
      setError('Error al cargar los casos facturados.');
    } finally {
      setLoadingCasos(false);
    }
  }, [fechaDesde, fechaHasta]);

  useEffect(() => { loadCasos(); /* eslint-disable-next-line */ }, []);

  // ── load abogados ─────────────────────────────────────────────────────────────
  const loadAbogados = useCallback(async () => {
    setLoadingAb(true);
    try {
      const data = await api.get('/abogado-facturacion');
      setAbogadosList(Array.isArray(data) ? data : []);
    } catch {
      setError('Error al cargar datos de abogados.');
    } finally {
      setLoadingAb(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'abogados') loadAbogados();
  }, [tab, loadAbogados]);

  // ── filtered + totals ────────────────────────────────────────────────────────
  const casosFiltrados = casos.filter(c => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return (
      String(c.expediente || '').toLowerCase().includes(q) ||
      (c.abogado            || '').toLowerCase().includes(q) ||
      (c.asunto             || '').toLowerCase().includes(q) ||
      (c.aseguradora        || '').toLowerCase().includes(q) ||
      (c.nombre_facturacion || '').toLowerCase().includes(q)
    );
  });

  const totalFirma   = casosFiltrados.reduce((s, c) => s + (Number(c.honorario_firma)   || 0), 0);
  const totalAbogado = casosFiltrados.reduce((s, c) => s + (Number(c.honorario_abogado) || 0), 0);

  // Paginacion (Diner 2026-04-27): si hay >50 casos, mostrar paginas como en Casos.jsx
  const totalPagesCasos = Math.max(1, Math.ceil(casosFiltrados.length / ITEMS_PER_PAGE_CASOS));
  const casosPaginados = casosFiltrados.slice(
    (currentPageCasos - 1) * ITEMS_PER_PAGE_CASOS,
    currentPageCasos * ITEMS_PER_PAGE_CASOS,
  );
  // Reset pagina al filtrar/buscar/cambiar fechas
  useEffect(() => { setCurrentPageCasos(1); }, [busqueda, fechaDesde, fechaHasta, casos.length]);

  // ── excel download ───────────────────────────────────────────────────────────
  const handleDownloadExcel = async () => {
    setLoadingExcel(true); setError('');
    try {
      const params = {};
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      const resp = await apiClient.get('/facturacion-legal-detalle/excel', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `facturacion_${fechaDesde}_${fechaHasta}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setError('Error al descargar el Excel.'); }
    finally { setLoadingExcel(false); }
  };

  // ── PDF download ─────────────────────────────────────────────────────────────
  const handleDownloadPDF = async (cc, filename) => {
    setError('');
    try {
      const resp = await apiClient.get(`/cuenta-cobro/${cc}/download/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setError('Error al descargar el PDF.'); }
  };

  // ── cuentas de cobro ─────────────────────────────────────────────────────────
  const handleLoadCuentas = async () => {
    if (!ccInput.trim()) return;
    setLoadingCuentas(true); setError(''); setCuentasData(null);
    try {
      const data = await api.get(`/cuenta-cobro/${ccInput.trim()}`);
      setCuentasData(data);
    } catch { setError('No se encontraron cuentas de cobro para esa cédula.'); }
    finally { setLoadingCuentas(false); }
  };

  const handleDeleteCC = async (cc, filename) => {
    if (!window.confirm(`¿Eliminar la cuenta de cobro "${filename}"? Esta acción no se puede deshacer.`)) return;
    setError('');
    try {
      await api.delete(`/cuenta-cobro/${cc}/file/${encodeURIComponent(filename)}`);
      handleLoadCuentas();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'No se pudo eliminar la cuenta de cobro.';
      setError(msg);
    }
  };

  // ── tab 'pagados' (Casos pagados) ───────────────────────────────────────────
  const [pagSearchAseg, setPagSearchAseg] = useState('');
  const [pagSearchExp, setPagSearchExp] = useState('');
  const [pagState, setPagState] = useState(null); // { aseguradora, expediente, pagado_en, pagado_por_nickname }
  const [pagLoading, setPagLoading] = useState(false);
  const [pagBulkFile, setPagBulkFile] = useState(null);
  const [pagBulkUploading, setPagBulkUploading] = useState(false);
  const [pagBulkResult, setPagBulkResult] = useState(null);
  const pagBulkInputRef = useRef(null);

  const handleBuscarCasoPagado = async () => {
    const aseg = pagSearchAseg.trim().toLowerCase();
    const exp  = pagSearchExp.trim();
    if (!aseg || !exp) { setError('Aseguradora y expediente son requeridos.'); return; }
    setPagLoading(true); setError(''); setPagState(null);
    try {
      const data = await api.get(`/factura?expediente=${encodeURIComponent(exp)}`);
      const match = (Array.isArray(data) ? data : []).find(c =>
        (c.expediente || '').trim() === exp &&
        (c.aseguradora || '').toLowerCase().trim() === aseg
      );
      if (!match) { setError('Expediente no encontrado para esa aseguradora.'); return; }
      setPagState({
        aseguradora: match.aseguradora,
        expediente: match.expediente,
        pagado_en: match.pagado_en || null,
        pagado_por_nickname: match.pagado_por_nickname || null,
      });
    } catch (err) {
      setError(err?.response?.data?.error || 'Error consultando el caso.');
    } finally { setPagLoading(false); }
  };

  const handleMarcarPagado = async () => {
    if (!pagState) return;
    setError('');
    try {
      const data = await api.post(`/casos/${encodeURIComponent(pagState.aseguradora.toLowerCase())}/${encodeURIComponent(pagState.expediente)}/marcar-pagado`);
      setPagState(s => s ? ({ ...s, pagado_en: data.pagado_en, pagado_por_nickname: 'tú' }) : s);
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo marcar como pagado.');
    }
  };

  const handleDesmarcarPagado = async () => {
    if (!pagState) return;
    if (!window.confirm(`¿Desmarcar el pago de ${pagState.expediente}? Volverá a ser editable en Facturación.`)) return;
    setError('');
    try {
      await api.post(`/casos/${encodeURIComponent(pagState.aseguradora.toLowerCase())}/${encodeURIComponent(pagState.expediente)}/marcar-no-pagado`);
      setPagState(s => s ? ({ ...s, pagado_en: null, pagado_por_nickname: null }) : s);
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo desmarcar.');
    }
  };

  const handlePagBulkUpload = async () => {
    if (!pagBulkFile) { setError('Selecciona un archivo .xlsx primero.'); return; }
    setPagBulkUploading(true); setError(''); setPagBulkResult(null);
    try {
      const form = new FormData();
      form.append('archivo', pagBulkFile);
      const data = await apiClient.post('/casos/marcar-pagado-bulk', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPagBulkResult(data?.data || data);
      setPagBulkFile(null);
      if (pagBulkInputRef.current) pagBulkInputRef.current.value = '';
    } catch (err) {
      setError(err?.response?.data?.error || 'Error procesando el archivo.');
    } finally { setPagBulkUploading(false); }
  };

  const handlePagDownloadTemplate = () => {
    // Genera xlsx mínimo con las dos columnas requeridas
    const csvContent = 'aseguradora,expediente\nIKE,EJEMPLO-001\nASISSPREX,EJEMPLO-002';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_marcar_pagado.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── bulk upload ──────────────────────────────────────────────────────────────
  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setLoadingBulk(true); setError(''); setBulkResult(null);
    try {
      const form = new FormData();
      form.append('archivo', bulkFile);
      const data = await apiClient.post('/abogado-facturacion/bulk', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(data.data);
      setSuccess(`Carga masiva completada: ${data.data.actualizados} actualizados.`);
      setBulkFile(null);
      if (bulkInputRef.current) bulkInputRef.current.value = '';
      loadAbogados();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Error al procesar el archivo.';
      setError(msg);
    } finally { setLoadingBulk(false); }
  };

  const handleDownloadTemplate = async () => {
    try {
      const resp = await apiClient.get('/abogado-facturacion/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'plantilla_consecutivos.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setError('Error al descargar la plantilla.'); }
  };

  // ── abogado form ─────────────────────────────────────────────────────────────
  const openEdit = (ab) => {
    setEditingNumDoc(ab.num_doc);
    setEditConsecutivo(ab.no_ccobro ?? 1);
  };
  const handleSaveAbogado = async () => {
    if (!editingNumDoc) return;
    setSavingAb(true); setError('');
    try {
      await api.post(`/abogado-facturacion/${editingNumDoc}`, {
        no_ccobro: Number(editConsecutivo) || 1,
      });
      setSuccess('Consecutivo actualizado correctamente.');
      setEditingNumDoc(null);
      loadAbogados();
    } catch { setError('Error al guardar el consecutivo.'); }
    finally { setSavingAb(false); }
  };

  // ── doc upload ───────────────────────────────────────────────────────────────
  const handleUploadDoc = async () => {
    if (!docFile || !docCC.trim()) { setError('Selecciona un archivo y escribe la cédula del abogado.'); return; }
    setLoadingDoc(true); setError('');
    try {
      const form = new FormData();
      form.append('archivo',     docFile);
      form.append('descripcion', docDescripcion);
      await apiClient.post(`/abogado/${docCC.trim()}/documento`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess('Documento cargado exitosamente.');
      setDocFile(null); setDocDescripcion('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      // Refrescar listado si ya estabamos viendo esta cedula
      if (docsList && docsList.cc === docCC.trim()) handleListarDocs();
    } catch { setError('Error al cargar el documento.'); }
    finally { setLoadingDoc(false); }
  };

  // ── doc soporte: listado por cedula (Diner 2026-04-27) ─────────────────────
  const handleListarDocs = async () => {
    if (!docCC.trim()) return;
    setLoadingDocsList(true); setError('');
    try {
      const data = await api.get(`/abogado/${docCC.trim()}/documentos`);
      setDocsList(data);
    } catch { setError('No se pudieron listar los documentos.'); setDocsList(null); }
    finally { setLoadingDocsList(false); }
  };

  const handleDownloadDocSoporte = async (cc, filename) => {
    try {
      const resp = await apiClient.get(`/abogado/${cc}/documento/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { setError('Error al descargar el documento.'); }
  };

  // ── cuenta de cobro: subir PDF (Contabilidad → Abogado lo ve) ──────────────
  const handleUploadCuentaCobro = async () => {
    if (!ccInput.trim() || !ccUploadFile) { setError('Selecciona un PDF y escribe la cédula.'); return; }
    setCcUploading(true); setError('');
    try {
      const form = new FormData();
      form.append('archivo', ccUploadFile);
      await apiClient.post(`/cuenta-cobro/${ccInput.trim()}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSuccess('Cuenta de cobro cargada. El abogado ya la verá en su panel.');
      setCcUploadFile(null);
      if (ccUploadInputRef.current) ccUploadInputRef.current.value = '';
      handleLoadCuentas();
    } catch (e) {
      const msg = e?.response?.data?.error || 'Error al subir la cuenta de cobro.';
      setError(msg);
    } finally { setCcUploading(false); }
  };

  // ── tab switch ───────────────────────────────────────────────────────────────
  const switchTab = (id) => { setTab(id); setError(''); setSuccess(''); };

  // ── access guard ─────────────────────────────────────────────────────────────
  if (rol !== 'Contabilidad' && rol !== 'Administrador') {
    return (
      <div className="flex items-center justify-center h-full bg-gradient-to-br from-slate-950 to-slate-900">
        <div className="text-center p-8 max-w-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-800 flex items-center justify-center">
            <Calculator size={32} className="text-slate-500" />
          </div>
          <h2 className="text-xl font-bold text-slate-300">Acceso Restringido</h2>
          <p className="text-slate-500 mt-2 text-sm">Este módulo es exclusivo para el rol de Contabilidad.</p>
        </div>
      </div>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-950 to-slate-900 text-slate-100 overflow-hidden">

      {/* Header */}
      <div className="px-6 pt-5 pb-4 border-b border-slate-800 bg-slate-900/80 flex-shrink-0">
        <h1 className="text-2xl font-extrabold text-white tracking-tight flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-900/20">
            <Calculator size={20} className="text-white" />
          </div>
          Contabilidad
        </h1>
        <p className="text-slate-400 text-xs mt-0.5 ml-12">
          Facturación · Cuentas de cobro · Abogados · Documentos
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800 px-6 bg-slate-900/80 flex-shrink-0">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => switchTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
              tab === t.id
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-slate-500 hover:text-slate-200'
            }`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Alerts */}
      <div className="flex-shrink-0 px-6">
        {error && (
          <div className="mt-3 flex items-center gap-2 bg-red-900/30 border border-red-700/60 rounded-xl px-4 py-2 text-red-300 text-sm">
            <AlertCircle size={14} className="flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-200 transition-colors"><X size={13} /></button>
          </div>
        )}
        {success && (
          <div className="mt-3 flex items-center gap-2 bg-emerald-900/30 border border-emerald-700/60 rounded-xl px-4 py-2 text-emerald-300 text-sm">
            <CheckCircle size={14} className="flex-shrink-0" />
            <span className="flex-1">{success}</span>
            <button onClick={() => setSuccess('')} className="text-emerald-400 hover:text-emerald-200 transition-colors"><X size={13} /></button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 custom-scrollbar">

        {/* ════ TAB 1: CASOS FACTURADOS ════ */}
        {tab === 'facturados' && (
          <div className="flex flex-col gap-4">

            {/* Filters */}
            <div className={`${cardCls} p-4 flex flex-wrap items-end gap-3`}>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-500">Desde</label>
                <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} className={inputCls} />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-500">Hasta</label>
                <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} className={inputCls} />
              </div>
              <button onClick={loadCasos} disabled={loadingCasos} className={btnPrimary}>
                {loadingCasos ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Buscar
              </button>
              <div className="flex-1 min-w-[200px] relative">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Filtrar por expediente, abogado, asunto…"
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  className={`${inputCls} pl-8 w-full`}
                />
              </div>
              <button onClick={handleDownloadExcel} disabled={loadingExcel} className={`${btnGreen} ml-auto`}>
                {loadingExcel ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                Excel
              </button>
            </div>

            {/* Totals */}
            {casos.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {[
                  { label: 'Casos',        val: casosFiltrados.length, color: 'text-white' },
                  { label: 'Hon. Firma',   val: formatCOP(totalFirma),    color: 'text-emerald-300' },
                  { label: 'Hon. Abogado', val: formatCOP(totalAbogado),  color: 'text-blue-300' },
                ].map(item => (
                  <div key={item.label} className={`${cardCls} px-4 py-2.5 text-sm`}>
                    <span className="text-slate-400">{item.label}: </span>
                    <span className={`font-semibold ${item.color}`}>{item.val}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Table */}
            {loadingCasos ? (
              <div className="flex items-center justify-center gap-2 py-20 text-slate-400">
                <Loader2 size={24} className="animate-spin text-blue-500" />
                <span className="text-sm">Cargando casos...</span>
              </div>
            ) : casosFiltrados.length === 0 ? (
              <div className={`${cardCls} flex flex-col items-center justify-center py-20`}>
                <div className="w-14 h-14 rounded-2xl bg-slate-800 flex items-center justify-center mb-4">
                  <FileText size={28} className="text-slate-500" />
                </div>
                <p className="text-slate-400 font-medium">Sin casos facturados</p>
                <p className="text-slate-600 text-sm mt-1">Ajusta el rango de fechas y busca nuevamente</p>
              </div>
            ) : (
              <div className="rounded-3xl overflow-hidden border border-slate-700/50 bg-slate-900/90 shadow-xl overflow-x-auto">
                <table className="w-full text-xs min-w-[1400px]">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-800/70">
                      {['Aseguradora','Expediente','Fecha','Cliente Corp.','Cuenta','Tipo Servicio','Asunto','Abogado','Kilometraje','Hon. Firma','Hon. Abogado','Cód. Fact.','Nombre Fact.','Categoría'].map(h => (
                        <th key={h} className="px-3 py-3 text-left text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {casosPaginados.map((c, i) => (
                      <tr key={`${c.aseguradora}_${c.expediente}_${i}`}
                        className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors duration-150">
                        <td className="px-3 py-2.5 whitespace-nowrap text-slate-300">{c.aseguradora || '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap font-mono text-blue-300 font-semibold">{c.expediente || '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-slate-400">{c.fecha || '—'}</td>
                        <td className="px-3 py-2.5 max-w-[130px] truncate text-slate-300">{c.cliente_corporativo || '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-slate-400">{c.cuenta || '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-slate-300">{c.tipo_servicio || '—'}</td>
                        <td className="px-3 py-2.5 max-w-[160px] truncate text-slate-400">{c.asunto || '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-slate-300">{c.abogado || '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-right font-mono text-slate-400">{c.kilometraje ?? '—'}</td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap text-emerald-400 font-mono font-medium">
                          {formatCOP(c.honorario_firma)}
                        </td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap text-blue-400 font-mono font-medium">
                          {formatCOP(c.honorario_abogado)}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-slate-400">{c.codigo_facturacion || '—'}</td>
                        <td className="px-3 py-2.5 max-w-[130px] truncate text-slate-400">{c.nombre_facturacion || '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-slate-400">{c.categoria_factura || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Paginacion casos facturados — replica patron de Casos.jsx */}
            {!loadingCasos && casosFiltrados.length > ITEMS_PER_PAGE_CASOS && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 py-3 rounded-2xl border border-slate-700/50 bg-slate-900/80">
                <span className="text-xs text-slate-400 text-center sm:text-left">
                  {((currentPageCasos - 1) * ITEMS_PER_PAGE_CASOS) + 1}–{Math.min(currentPageCasos * ITEMS_PER_PAGE_CASOS, casosFiltrados.length)} de {casosFiltrados.length} casos
                </span>
                <div className="flex items-center justify-center gap-1">
                  <button onClick={() => setCurrentPageCasos(1)} disabled={currentPageCasos === 1} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">«</button>
                  <button onClick={() => setCurrentPageCasos(p => Math.max(1, p - 1))} disabled={currentPageCasos === 1} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">‹</button>
                  {Array.from({ length: Math.min(5, totalPagesCasos) }, (_, i) => {
                    const start = Math.max(1, Math.min(currentPageCasos - 2, totalPagesCasos - 4));
                    const page = start + i;
                    return page <= totalPagesCasos ? (
                      <button key={page} onClick={() => setCurrentPageCasos(page)} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${currentPageCasos === page ? 'bg-blue-600 text-white' : 'text-slate-400 hover:bg-slate-700'}`}>{page}</button>
                    ) : null;
                  })}
                  <button onClick={() => setCurrentPageCasos(p => Math.min(totalPagesCasos, p + 1))} disabled={currentPageCasos === totalPagesCasos} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">›</button>
                  <button onClick={() => setCurrentPageCasos(totalPagesCasos)} disabled={currentPageCasos === totalPagesCasos} className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-30 transition-colors">»</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ════ TAB 2: CUENTAS DE COBRO ════ */}
        {tab === 'cuentas' && (
          <div className="flex flex-col gap-4 max-w-3xl">
            <div className={`${cardCls} p-4 flex items-end gap-3`}>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs font-medium text-slate-500">Cédula del abogado (CC)</label>
                <input
                  type="text"
                  placeholder="Ej: 1234567890"
                  value={ccInput}
                  onChange={e => setCcInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleLoadCuentas()}
                  className={inputCls}
                />
              </div>
              <button onClick={handleLoadCuentas} disabled={loadingCuentas || !ccInput.trim()} className={btnPrimary}>
                {loadingCuentas ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Buscar
              </button>
            </div>

            {/* Subir cuenta de cobro PDF para que el abogado la vea */}
            <div className={`${cardCls} p-4`}>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-slate-200">
                <Upload size={14} className="text-emerald-400" />
                Cargar cuenta de cobro PDF (la verá el abogado)
              </h3>
              <div className="flex items-center gap-3">
                <div
                  onClick={() => ccUploadInputRef.current?.click()}
                  className={`flex-1 border-2 border-dashed rounded-xl px-4 py-3 cursor-pointer transition-colors text-sm flex items-center gap-3 ${
                    ccUploadFile ? 'border-emerald-600 bg-emerald-900/20' : 'border-slate-700 hover:border-blue-500 bg-slate-800/40'
                  }`}
                >
                  <FileText size={16} className={ccUploadFile ? 'text-emerald-400' : 'text-slate-600'} />
                  {ccUploadFile
                    ? <><span className="font-medium text-slate-200 truncate">{ccUploadFile.name}</span><span className="text-xs text-slate-400 ml-auto flex-shrink-0">{(ccUploadFile.size / 1024).toFixed(1)} KB</span></>
                    : <span className="text-slate-500">Seleccionar PDF · requiere cédula arriba</span>}
                </div>
                <input
                  ref={ccUploadInputRef}
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={e => setCcUploadFile(e.target.files[0] || null)}
                />
                {ccUploadFile && (
                  <button
                    onClick={e => { e.stopPropagation(); setCcUploadFile(null); if (ccUploadInputRef.current) ccUploadInputRef.current.value = ''; }}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                  ><X size={15} /></button>
                )}
                <button onClick={handleUploadCuentaCobro} disabled={!ccUploadFile || !ccInput.trim() || ccUploading} className={btnGreen}>
                  {ccUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Subir
                </button>
              </div>
            </div>

            {cuentasData ? (
              <div className={`${cardCls} overflow-hidden`}>
                <div className="px-4 py-3 bg-slate-800/60 border-b border-slate-700/50 flex items-center gap-3">
                  <DollarSign size={15} className="text-blue-500" />
                  <span className="text-sm font-semibold text-white">CC: {cuentasData.cc}</span>
                  <span className="text-xs text-slate-400">{cuentasData.total_archivos} archivo(s)</span>
                </div>
                {!cuentasData.archivos?.length ? (
                  <div className="py-10 text-center text-slate-400 text-sm">Sin archivos para esta cédula</div>
                ) : (
                  <div className="divide-y divide-slate-800/60">
                    {cuentasData.archivos.map((archivo, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-800/40 transition-colors duration-150">
                        <div className="flex items-center gap-3">
                          <FileText size={16} className="text-red-400 flex-shrink-0" />
                          <span className="text-sm font-mono text-slate-200">{archivo}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDownloadPDF(cuentasData.cc, archivo)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-xl text-xs font-medium transition-colors active:scale-95"
                          >
                            <FileDown size={13} /> Descargar
                          </button>
                          <button
                            onClick={() => handleDeleteCC(cuentasData.cc, archivo)}
                            title="Eliminar (solo PDFs cargados manualmente; los definitivos quedan bloqueados)"
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-900/40 hover:bg-rose-800/60 border border-rose-800/60 text-rose-200 rounded-xl text-xs font-medium transition-colors active:scale-95"
                          >
                            <Trash2 size={13} /> Eliminar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : !loadingCuentas && (
              <div className={`${cardCls} flex flex-col items-center justify-center py-20`}>
                <div className="w-14 h-14 rounded-2xl bg-slate-800 flex items-center justify-center mb-4">
                  <DollarSign size={28} className="text-slate-500" />
                </div>
                <p className="text-slate-400 font-medium">Sin cuentas de cobro</p>
                <p className="text-slate-600 text-sm mt-1">Ingresa la cédula del abogado para consultar</p>
              </div>
            )}
          </div>
        )}

        {/* ════ TAB: CASOS PAGADOS ════ */}
        {tab === 'pagados' && (
          <div className="flex flex-col gap-4 max-w-4xl">
            {/* Bulk upload xlsx */}
            <div className={`${cardCls} p-4`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-200">
                  <Upload size={14} className="text-emerald-400" />
                  Marcar pagados en lote (Excel/CSV)
                </h3>
                <button onClick={handlePagDownloadTemplate} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-xl text-xs font-medium transition-colors active:scale-95">
                  <Download size={12} /> Plantilla
                </button>
              </div>
              <p className="text-xs text-slate-400 mb-3">
                El archivo debe tener primera fila con headers <span className="font-mono text-slate-200">aseguradora</span> y <span className="font-mono text-slate-200">expediente</span>.
                Cada fila marca un caso como pagado (idempotente: si ya estaba pagado se actualiza el usuario).
              </p>
              <div className="flex items-center gap-3">
                <div
                  onClick={() => pagBulkInputRef.current?.click()}
                  className={`flex-1 border-2 border-dashed rounded-xl px-4 py-3 cursor-pointer transition-colors text-sm flex items-center gap-3 ${
                    pagBulkFile ? 'border-emerald-600 bg-emerald-900/20' : 'border-slate-700 hover:border-blue-500 bg-slate-800/40'
                  }`}
                >
                  <FileText size={16} className={pagBulkFile ? 'text-emerald-400' : 'text-slate-600'} />
                  {pagBulkFile
                    ? <><span className="font-medium text-slate-200 truncate">{pagBulkFile.name}</span><span className="text-xs text-slate-400 ml-auto flex-shrink-0">{(pagBulkFile.size / 1024).toFixed(1)} KB</span></>
                    : <span className="text-slate-500">Seleccionar archivo .xlsx (con headers aseguradora, expediente)</span>}
                </div>
                <input ref={pagBulkInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => setPagBulkFile(e.target.files?.[0] || null)} />
                <button onClick={handlePagBulkUpload} disabled={!pagBulkFile || pagBulkUploading} className={btnGreen}>
                  {pagBulkUploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Procesar
                </button>
              </div>
              {pagBulkResult && (
                <div className="mt-3 p-3 rounded-xl bg-slate-800/60 border border-slate-700/50 text-xs">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-emerald-400 font-semibold">{pagBulkResult.ok || 0} OK</span>
                    <span className="text-rose-400 font-semibold">{pagBulkResult.errores || 0} con error</span>
                  </div>
                  {Array.isArray(pagBulkResult.errores_detalle) && pagBulkResult.errores_detalle.length > 0 && (
                    <details className="mt-1">
                      <summary className="cursor-pointer text-slate-300 text-xs">Ver errores ({pagBulkResult.errores_detalle.length})</summary>
                      <ul className="mt-2 space-y-1 max-h-40 overflow-auto">
                        {pagBulkResult.errores_detalle.map((e, i) => (
                          <li key={i} className="text-slate-400">Fila {e.row}: {e.aseguradora}/{e.expediente} → <span className="text-rose-300">{e.error}</span></li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>

            {/* Single mark/unmark */}
            <div className={`${cardCls} p-4`}>
              <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-200 mb-3">
                <Search size={14} className="text-blue-400" />
                Marcar un caso individual
              </h3>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[160px]">
                  <label className="text-xs font-medium text-slate-500">Aseguradora</label>
                  <input
                    type="text"
                    placeholder="IKE / ASISSPREX / ..."
                    value={pagSearchAseg}
                    onChange={e => setPagSearchAseg(e.target.value)}
                    className={inputCls + ' w-full mt-1'}
                  />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <label className="text-xs font-medium text-slate-500">Expediente</label>
                  <input
                    type="text"
                    placeholder="Ej. 2025-IKE-12345"
                    value={pagSearchExp}
                    onChange={e => setPagSearchExp(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleBuscarCasoPagado()}
                    className={inputCls + ' w-full mt-1'}
                  />
                </div>
                <button onClick={handleBuscarCasoPagado} disabled={pagLoading || !pagSearchAseg.trim() || !pagSearchExp.trim()} className={btnPrimary}>
                  {pagLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                  Buscar
                </button>
              </div>
              {pagState && (
                <div className="mt-4 p-4 rounded-xl bg-slate-800/60 border border-slate-700/50">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-white truncate">{pagState.aseguradora} · {pagState.expediente}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {pagState.pagado_en
                          ? <>Pagado el <span className="text-emerald-300">{new Date(pagState.pagado_en).toLocaleString('es-CO')}</span>{pagState.pagado_por_nickname ? <> por <span className="text-emerald-300">{pagState.pagado_por_nickname}</span></> : null}</>
                          : <span className="text-slate-500">Sin marca de pago</span>
                        }
                      </div>
                    </div>
                    {pagState.pagado_en
                      ? <button onClick={handleDesmarcarPagado} className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-900/40 hover:bg-rose-800/60 border border-rose-800/60 text-rose-200 rounded-xl text-xs font-medium transition-colors active:scale-95 flex-shrink-0">
                          <RotateCcw size={13} /> Desmarcar
                        </button>
                      : <button onClick={handleMarcarPagado} className={btnGreen + ' flex-shrink-0'}>
                          <Lock size={14} /> Marcar pagado
                        </button>
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════ TAB 3: ABOGADOS ════ */}
        {tab === 'abogados' && (
          <div className="flex flex-col gap-4 max-w-3xl">

            {/* Carga masiva Excel */}
            <div className={`${cardCls} p-4`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold flex items-center gap-2 text-slate-200">
                  <Upload size={14} className="text-emerald-400" />
                  Carga masiva de consecutivos
                </h3>
                <button onClick={handleDownloadTemplate} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-xl text-xs font-medium transition-colors active:scale-95">
                  <Download size={12} /> Plantilla Excel
                </button>
              </div>

              <div className="flex items-center gap-3">
                {/* drop zone / file selector */}
                <div
                  onClick={() => bulkInputRef.current?.click()}
                  className={`flex-1 border-2 border-dashed rounded-xl px-4 py-3 cursor-pointer transition-colors text-sm flex items-center gap-3 ${
                    bulkFile
                      ? 'border-emerald-600 bg-emerald-900/20'
                      : 'border-slate-700 hover:border-blue-500 bg-slate-800/40'
                  }`}
                >
                  <FileText size={16} className={bulkFile ? 'text-emerald-400' : 'text-slate-600'} />
                  {bulkFile
                    ? <><span className="font-medium text-slate-200 truncate">{bulkFile.name}</span><span className="text-xs text-slate-400 ml-auto flex-shrink-0">{(bulkFile.size / 1024).toFixed(1)} KB</span></>
                    : <span className="text-slate-500">Seleccionar Excel (.xlsx / .xls)</span>
                  }
                </div>
                <input
                  ref={bulkInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={e => { setBulkFile(e.target.files[0] || null); setBulkResult(null); }}
                />
                {bulkFile && (
                  <button
                    onClick={e => { e.stopPropagation(); setBulkFile(null); setBulkResult(null); if (bulkInputRef.current) bulkInputRef.current.value = ''; }}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                  ><X size={15} /></button>
                )}
                <button onClick={handleBulkUpload} disabled={!bulkFile || loadingBulk} className={btnGreen}>
                  {loadingBulk ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Cargar
                </button>
              </div>

              {/* resultado */}
              {bulkResult && (
                <div className="mt-3 rounded-xl bg-slate-800/60 border border-slate-700 px-4 py-3 text-xs space-y-1">
                  <p className="font-semibold text-emerald-300">
                    ✓ {bulkResult.actualizados} abogado(s) actualizados
                  </p>
                  {bulkResult.no_encontrados?.length > 0 && (
                    <p className="text-amber-400">
                      ⚠ No encontrados ({bulkResult.no_encontrados.length}): {bulkResult.no_encontrados.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Search + reload */}
            <div className={`${cardCls} p-4 flex items-center gap-3`}>
              <div className="relative flex-1">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o cédula…"
                  value={busquedaAb}
                  onChange={e => setBusquedaAb(e.target.value)}
                  className={`${inputCls} pl-8 w-full`}
                />
              </div>
              <button onClick={loadAbogados} disabled={loadingAb} className={btnPrimary}>
                {loadingAb ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                Recargar
              </button>
            </div>

            {/* Table */}
            {loadingAb ? (
              <div className="flex justify-center py-12"><Loader2 size={28} className="animate-spin text-blue-500" /></div>
            ) : abogadosList.length === 0 ? (
              <div className={`${cardCls} flex flex-col items-center justify-center py-16`}>
                <div className="w-14 h-14 rounded-2xl bg-slate-800 flex items-center justify-center mb-4">
                  <Users size={28} className="text-slate-500" />
                </div>
                <p className="text-slate-400 font-medium">Sin abogados registrados</p>
              </div>
            ) : (
              <div className="rounded-3xl overflow-hidden border border-slate-700/50 bg-slate-900/90 shadow-xl">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700 bg-slate-800/70 text-slate-400 text-[10px] uppercase tracking-wider">
                      <th className="px-4 py-3 text-left font-semibold">Cédula (CC)</th>
                      <th className="px-4 py-3 text-left font-semibold">Nombre</th>
                      <th className="px-4 py-3 text-center font-semibold">Consecutivo</th>
                      <th className="px-4 py-3 text-center font-semibold">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {abogadosList
                      .filter(ab => {
                        if (!busquedaAb) return true;
                        const q = busquedaAb.toLowerCase();
                        return (ab.nombre || '').toLowerCase().includes(q) ||
                               String(ab.num_doc || '').includes(q);
                      })
                      .map(ab => (
                        <tr key={ab.num_doc} className="border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors duration-150">
                          <td className="px-4 py-3 font-mono text-slate-400 text-xs">{ab.num_doc}</td>
                          <td className="px-4 py-3 font-medium text-slate-200">{ab.nombre || '—'}</td>
                          <td className="px-4 py-3 text-center">
                            {editingNumDoc === ab.num_doc ? (
                              <input
                                type="number"
                                min={1}
                                value={editConsecutivo}
                                onChange={e => setEditConsecutivo(e.target.value)}
                                className={`${inputCls} w-20 text-center`}
                                autoFocus
                              />
                            ) : (
                              <span className="inline-flex items-center justify-center w-10 h-7 bg-blue-900/30 text-blue-300 rounded-lg text-xs font-bold">
                                {ab.no_ccobro ?? 1}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {editingNumDoc === ab.num_doc ? (
                              <div className="flex items-center justify-center gap-1.5">
                                <button
                                  onClick={handleSaveAbogado}
                                  disabled={savingAb}
                                  className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-medium transition-colors active:scale-95"
                                >
                                  {savingAb ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                                  Guardar
                                </button>
                                <button
                                  onClick={() => setEditingNumDoc(null)}
                                  className="px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl text-xs font-medium transition-colors active:scale-95"
                                >
                                  Cancelar
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => openEdit(ab)}
                                className="p-1.5 text-slate-400 hover:text-blue-400 transition-colors"
                              >
                                <Edit3 size={15} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ════ TAB 4: DOCUMENTOS DE SOPORTE ════ */}
        {tab === 'documentos' && (
          <div className="flex flex-col gap-4 max-w-xl">
            <div className={`${cardCls} p-5`}>
              <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                <Upload size={15} className="text-blue-500" />
                Cargar documento de soporte de pago
              </h3>

              <div className="flex flex-col gap-1 mb-4">
                <label className="text-xs font-medium text-slate-500">Cédula del abogado (CC) *</label>
                <input
                  type="text"
                  placeholder="Ej: 1234567890"
                  value={docCC}
                  onChange={e => setDocCC(e.target.value)}
                  className={inputCls}
                />
              </div>

              {/* Drop zone */}
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-700 hover:border-blue-500 rounded-2xl p-8 text-center cursor-pointer transition-colors mb-4"
              >
                {docFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText size={20} className="text-blue-500 flex-shrink-0" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-200">{docFile.name}</p>
                      <p className="text-xs text-slate-400">{(docFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={e => { e.stopPropagation(); setDocFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="ml-2 text-slate-400 hover:text-red-500 transition-colors"
                    ><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <Upload size={26} className="mx-auto mb-2 text-slate-600" />
                    <p className="text-sm text-slate-400">Haz clic para seleccionar</p>
                    <p className="text-xs text-slate-600 mt-1">PDF, Excel, imágenes</p>
                  </>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.xlsx,.xls,.png,.jpg,.jpeg,.docx"
                onChange={e => setDocFile(e.target.files[0] || null)}
              />

              <div className="flex flex-col gap-1 mb-4">
                <label className="text-xs font-medium text-slate-500">Descripción (opcional)</label>
                <textarea
                  rows={2}
                  value={docDescripcion}
                  onChange={e => setDocDescripcion(e.target.value)}
                  placeholder="Comprobante de pago, soporte de transferencia…"
                  className={`${inputCls} resize-none`}
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleUploadDoc}
                  disabled={!docFile || !docCC.trim() || loadingDoc}
                  className={`flex-1 justify-center ${btnPrimary}`}
                >
                  {loadingDoc ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {loadingDoc ? 'Cargando…' : 'Cargar Documento'}
                </button>
                <button
                  onClick={handleListarDocs}
                  disabled={!docCC.trim() || loadingDocsList}
                  className={`justify-center ${btnGreen}`}
                  title="Ver documentos ya cargados a esta cédula"
                >
                  {loadingDocsList ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                  Ver cargados
                </button>
              </div>
            </div>

            {/* Listado de docs ya subidos a la cedula consultada */}
            {docsList && (
              <div className={`${cardCls} overflow-hidden`}>
                <div className="px-4 py-3 bg-slate-800/60 border-b border-slate-700/50 flex items-center gap-3">
                  <FileText size={15} className="text-emerald-400" />
                  <span className="text-sm font-semibold text-white">CC: {docsList.cc}</span>
                  <span className="text-xs text-slate-400">{docsList.total} documento(s)</span>
                </div>
                {!docsList.archivos?.length ? (
                  <div className="py-10 text-center text-slate-400 text-sm">Aún no hay documentos para esta cédula</div>
                ) : (
                  <div className="divide-y divide-slate-800/60">
                    {docsList.archivos.map((archivo, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-800/40 transition-colors duration-150">
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText size={16} className="text-emerald-400 flex-shrink-0" />
                          <span className="text-sm font-mono text-slate-200 truncate">{archivo}</span>
                        </div>
                        <button
                          onClick={() => handleDownloadDocSoporte(docsList.cc, archivo)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-xl text-xs font-medium transition-colors active:scale-95 ml-2 flex-shrink-0"
                        >
                          <Download size={13} /> Descargar
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Contabilidad;
